import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import Footer from "../SharedComponents/Footer";
import PlacesSection from "../SharedComponents/PlaceSection";
import Navbar from "./Navbar";
import CityWeatherPanel from "./CityWeatherPanel";
import BackButton from "./BackButton";
import { getCachedPlaceImageUrls } from "../services/placeImageCache";
import { trackEvent } from "../utils/analytics";
import useUserStore from "../store/userStore";
import { useFavorite } from "../hooks/useFavorites";
import CityPlacesMap from "./CityPlacesMap";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface City {
    id: number;
    name: string;
    place_count: number;
    description?: string;
    image?: string;
    latitude?: number | null;
    longitude?: number | null;
}

interface SpecialExperience {
    place_id: string;
    name: string;
    description?: string;
    images?: { file_url: string }[];
}

const CITY_SECTIONS = [
    { title: (city: string) => `جاذبه‌های دیدنی ${city}`, category: "جاذبه‌های دیدنی" },
    { title: (city: string) => `رستوران‌های ${city}`, category: "رستوران" },
    { title: (city: string) => `کافه‌های ${city}`, category: "کافه" },
    { title: (city: string) => `اقامتگاه‌های ${city}`, category: "اقامتگاه" },
];

const CityAboutSkeleton: React.FC = () => (
    <section className="site-container pb-10 pt-12" aria-label="در حال دریافت معرفی شهر" aria-busy="true">
        <div className="mx-auto max-w-4xl animate-pulse text-center">
            <div className="mx-auto h-7 w-40 rounded bg-gray-200" />
            <div className="mt-6 space-y-3">
                <div className="mx-auto h-3 w-full rounded bg-gray-200" />
                <div className="mx-auto h-3 w-11/12 rounded bg-gray-200" />
                <div className="mx-auto h-3 w-10/12 rounded bg-gray-200" />
                <div className="mx-auto h-3 w-3/4 rounded bg-gray-200" />
            </div>
        </div>
    </section>
);

const SpecialExperienceSkeleton: React.FC = () => (
    <aside className="min-h-[280px] animate-pulse rounded-2xl bg-gray-200 p-5" aria-label="در حال دریافت تجربه خاص" aria-busy="true">
        <div className="h-5 w-24 rounded-full bg-gray-300" />
        <div className="mt-5 h-6 w-3/4 rounded bg-gray-300" />
        <div className="mt-4 h-3 w-full rounded bg-gray-300" />
        <div className="mt-2 h-3 w-4/5 rounded bg-gray-300" />
        <div className="mt-20 h-9 w-28 rounded-lg bg-gray-300" />
    </aside>
);

const CityWeatherSkeleton: React.FC = () => (
    <section className="site-container mt-8" aria-label="در حال دریافت آب‌وهوای شهر" aria-busy="true">
        <div className="animate-pulse rounded-xl  p-4 tablet:p-5">
            <div className="h-6 w-40 rounded bg-gray-200" />
            <div className="mt-4 flex gap-2 overflow-hidden pb-1">
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="h-[104px] min-w-[96px] rounded-lg bg-gray-100" />
                ))}
            </div>
        </div>
    </section>
);

const CityPage: React.FC = () => {
    const { city } = useParams<{ city: string }>();
    const decodedCity = decodeURIComponent(city ?? "");
    const trackedCityRef = useRef<string | null>(null);
    const { user } = useUserStore();

    const { data: cityData, isLoading: cityLoading } = useQuery<City[], Error, City | undefined>({
        queryKey: ["cities"],
        queryFn: async () => {
            const res = await fetch(`${BASE_URL}/api/cities/`);
            if (!res.ok) throw new Error("خطا در دریافت شهرها");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        select: (cities) => cities.find((c) => c.name === decodedCity),
        staleTime: 10 * 60 * 1000,
    });

    const imageUrl = cityData?.image
        ? cityData.image.startsWith("http")
            ? cityData.image
            : `${BASE_URL}${cityData.image}`
        : null;

    const { isFavorite, isLoading: isFavoriteLoading, toggleFavorite } = useFavorite(
        user?.userId,
        "city",
        cityData ? String(cityData.id) : undefined,
    );

    const handleToggleFavorite = async () => {
        if (!cityData) return;

        if (!user?.userId) {
            toast.error("لطفاً ابتدا وارد حساب کاربری خود شوید", {
                className: "toast",
                hideProgressBar: true,
            });
            return;
        }

        if (isFavoriteLoading) return;

        try {
            const result = await toggleFavorite();
            toast.success(
                result.action === "added"
                    ? `${cityData.name} به علاقه‌مندی‌ها اضافه شد`
                    : `${cityData.name} از علاقه‌مندی‌ها حذف شد`,
                { className: "toast", hideProgressBar: true },
            );
        } catch {
            toast.error("تغییر علاقه‌مندی با خطا مواجه شد", {
                className: "toast",
                hideProgressBar: true,
            });
        }
    };

    useEffect(() => {
        if (!cityData?.name || trackedCityRef.current === cityData.name) return;

        trackedCityRef.current = cityData.name;
        trackEvent("view_city_guide", {
            city_name: cityData.name,
            city_id: cityData.id,
        });
    }, [cityData?.id, cityData?.name]);

    const hasImage = !!imageUrl;

    const { data: specialExperiences = [], isLoading: specialExperiencesLoading } = useQuery<SpecialExperience[]>({
        queryKey: ["places", "special-experiences", decodedCity],
        queryFn: async () => {
            const params = new URLSearchParams({
                category: "تجربه خاص",
                city: decodedCity,
                page: "1",
                page_size: "10",
            });
            const res = await fetch(`${BASE_URL}/api/places/?${params.toString()}`);
            if (!res.ok) throw new Error("خطا در دریافت تجربه‌های خاص شهر");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        enabled: Boolean(decodedCity),
        staleTime: 5 * 60 * 1000,
    });
    const [activeExperienceIndex, setActiveExperienceIndex] = useState(0);

    useEffect(() => {
        setActiveExperienceIndex(0);
    }, [decodedCity]);

    useEffect(() => {
        if (activeExperienceIndex >= specialExperiences.length) {
            setActiveExperienceIndex(0);
        }
    }, [activeExperienceIndex, specialExperiences.length]);

    const activeExperience = specialExperiences[activeExperienceIndex];
    const [activeExperienceImageUrl, setActiveExperienceImageUrl] = useState<string | null>(null);
    const [isActiveExperienceImageLoading, setIsActiveExperienceImageLoading] = useState(false);

    useEffect(() => {
        if (!activeExperience) {
            setActiveExperienceImageUrl(null);
            setIsActiveExperienceImageLoading(false);
            return;
        }

        let isCurrentExperience = true;
        const inlineImageUrl = activeExperience.images
            ?.map((image) => image.file_url)
            .find(Boolean);

        if (inlineImageUrl) {
            setActiveExperienceImageUrl(
                inlineImageUrl.startsWith("http") ? inlineImageUrl : `${BASE_URL}${inlineImageUrl}`,
            );
            setIsActiveExperienceImageLoading(false);
            return () => {
                isCurrentExperience = false;
            };
        }

        setIsActiveExperienceImageLoading(true);
        getCachedPlaceImageUrls(activeExperience.place_id).then((imageUrls) => {
            const firstImageUrl = imageUrls[0];
            if (isCurrentExperience) {
                setActiveExperienceImageUrl(
                    firstImageUrl
                        ? (firstImageUrl.startsWith("http") ? firstImageUrl : `${BASE_URL}${firstImageUrl}`)
                        : null,
                );
                setIsActiveExperienceImageLoading(false);
            }
        });

        return () => {
            isCurrentExperience = false;
        };
    }, [activeExperience]);

    useEffect(() => {
        if (specialExperiences.length < 2) return;

        const rotationTimer = window.setInterval(() => {
            setActiveExperienceIndex((current) =>
                current === specialExperiences.length - 1 ? 0 : current + 1,
            );
        }, 5000);

        return () => window.clearInterval(rotationTimer);
    }, [specialExperiences.length]);

    return (
        <div className="min-h-screen min-w-screen">
            
            <Navbar />

            <div className="site-frame relative mt-[72px] h-72 overflow-hidden md:h-80 desktop:mt-20 desktop:h-96 desktop:rounded-2xl">

                {/* Background */}
                {cityLoading ? (
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                ) : hasImage ? (
                    <img
                        src={imageUrl!}
                        alt={decodedCity}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-100" />
                )}

                {/* Gradient — only with image */}
                {hasImage && !cityLoading && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/20" />
                )}

                {/* Back action and desktop breadcrumb */}
                <div className="site-container absolute inset-x-0 top-4 z-10 flex items-center justify-between gap-3 desktop:px-6">
                    <BackButton
                        variant={hasImage && !cityLoading ? "overlay" : "surface"}
                        fallbackTo="/destinations"
                        reverseArrow
                    />

                    {cityData && (
                        <button
                            type="button"
                            onClick={() => void handleToggleFavorite()}
                            disabled={Boolean(user?.userId) && isFavoriteLoading}
                            aria-label={isFavorite ? "حذف شهر از علاقه‌مندی‌ها" : "افزودن شهر به علاقه‌مندی‌ها"}
                            aria-pressed={isFavorite}
                            title={isFavorite ? "حذف از علاقه‌مندی‌ها" : "افزودن به علاقه‌مندی‌ها"}
                            className={`flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${
                                isFavorite ? "text-[#E5484D]" : "text-black"
                            }`}
                        >
                            {Boolean(user?.userId) && isFavoriteLoading ? (
                                <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                            ) : (
                                <Heart size={18} fill={isFavorite ? "currentColor" : "none"} aria-hidden="true" />
                            )}
                        </button>
                    )}

                    {/* {!cityLoading && (
                        <nav className="hidden items-center gap-1.5 text-sm font-myYekanFaNumRegular tablet:flex">
                            <Link
                                to="/"
                                className={`transition ${hasImage ? "text-white/90 hover:text-white" : "text-gray-800 hover:text-gray-700"}`}
                            >
                                خانه
                            </Link>
                            <span className={hasImage ? "text-white/90" : "text-gray-800"}>&raquo;</span>
                            <Link
                                to="/destinations"
                                className={`transition ${hasImage ? "text-white/90 hover:text-white" : "text-gray-800 hover:text-gray-700"}`}
                            >
                                شهرها
                            </Link>
                            <span className={hasImage ? "text-white/90" : "text-gray-800"}>&raquo;</span>
                            <span className={`font-myYekanDemibold ${hasImage ? "text-white" : "text-gray-800"}`}>
                                {decodedCity}
                            </span>
                        </nav>
                    )} */}
                </div>

                {/* ── City name — anchored to bottom of hero ── */}
                {cityLoading ? (
                    <div className="site-container absolute inset-x-0 bottom-0 pb-6 desktop:px-6" aria-hidden="true">
                        <div className="h-8 w-36 animate-pulse rounded bg-gray-300/80" />
                        <div className="mt-2 h-4 w-20 animate-pulse rounded bg-gray-300/70" />
                    </div>
                ) : (
                    <div className="site-container absolute bottom-0 inset-x-0 pb-6 desktop:px-6">
                        <h1 className={`text-2xl md:text-3xl lg:text-4xl font-myYekanFaNumExtraBold ${hasImage ? "text-white" : "text-gray-900"}`}>
                            {decodedCity}
                        </h1>
                        {!!cityData?.place_count && (
                            <span className={`text-sm font-myYekanFaNumRegular mt-1 block ${hasImage ? "text-white/55" : "text-gray-400"}`}>
                                {cityData.place_count} مکان
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Page body ── */}
            <div className="max-w-8xl mx-auto">

                {/* {(cityLoading || specialExperiencesLoading || activeExperience) && (
                    <section className="site-container pt-5 tablet:pt-6" aria-label={`تجربه خاص ${decodedCity}`}>
                        <div>
                            {cityLoading || specialExperiencesLoading ? (
                                <SpecialExperienceSkeleton />
                            ) : activeExperience && (
                                <aside className={`relative min-h-[280px] overflow-hidden rounded-2xl text-white shadow-sm ${isActiveExperienceImageLoading && !activeExperienceImageUrl ? "bg-slate-900" : "bg-[#038545]"}`}>
                                    {activeExperienceImageUrl && (
                                        <img
                                            src={activeExperienceImageUrl}
                                            alt=""
                                            aria-hidden="true"
                                            className="absolute inset-0 z-0 h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    )}
                                    {activeExperienceImageUrl && (
                                        <div className="absolute inset-0 z-10 bg-black/55" aria-hidden="true" />
                                    )}
                                    {isActiveExperienceImageLoading && !activeExperienceImageUrl && (
                                        <div className="absolute inset-0 z-10 bg-white/10 p-5 backdrop-blur-md" aria-hidden="true">
                                            <div className="h-5 w-24 animate-pulse rounded-full bg-white/25" />
                                            <div className="mt-5 h-6 w-3/4 animate-pulse rounded bg-white/25" />
                                            <div className="mt-3 h-3 w-full animate-pulse rounded bg-white/15" />
                                            <div className="mt-2 h-3 w-4/5 animate-pulse rounded bg-white/15" />
                                        </div>
                                    )}
                                    <div className={`relative z-20 flex min-h-[280px] flex-col p-5 ${isActiveExperienceImageLoading && !activeExperienceImageUrl ? "opacity-0" : ""}`}>
                                        <span className="inline-flex w-fit rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-myYekanFaNumMedium">
                                           تجربه خاص در {decodedCity}
                                        </span>
                                        <h2 className="mt-3 line-clamp-2 font-myYekanFaNumDemiBold text-lg leading-7">
                                            {activeExperience.name}
                                        </h2>
                                        {activeExperience.description && (
                                            <p className="mt-2 line-clamp-3 text-xs leading-6 text-white/85">
                                                {activeExperience.description}
                                            </p>
                                        )}
                                        <div className="mt-auto pt-4">
                                            <Link
                                                to={`/places/${activeExperience.place_id}`}
                                                className="rounded-lg bg-[#cffae7] px-3 py-2 text-xs font-myYekanFaNumMedium text-[#075c35] transition-colors hover:bg-white"
                                            >
                                                مشاهده تجربه
                                            </Link>
                                        </div>
                                    </div>
                                </aside>
                            )}
                        </div>
                    </section>
                )} */}

                {cityLoading ? (
                    <CityWeatherSkeleton />
                ) : cityData?.id && (
                    <CityWeatherPanel
                        cityId={cityData.id}
                        cityName={cityData.name}
                        coordinatesAvailable={cityData.latitude != null && cityData.longitude != null}
                    />
                )}

                <div className="mt-8">
                    {/* Place sections */}
                    {CITY_SECTIONS.map(({ title, category }, index) => (
                        <React.Fragment key={category}>
                            <PlacesSection
                                title={title(decodedCity)}
                                category={category}
                                city={decodedCity}
                                hideIfEmpty
                                showArrowsOnHover
                            />
                            {index === 0 && cityData?.id && cityData.latitude != null && cityData.longitude != null && (
                                <CityPlacesMap
                                    cityId={cityData.id}
                                    cityName={cityData.name}
                                    latitude={cityData.latitude}
                                    longitude={cityData.longitude}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {cityLoading ? (
                    <CityAboutSkeleton />
                ) : cityData?.description ? (
                    <section className="site-container pb-10 pt-12" aria-labelledby="city-about-title">
                        <article className="mx-auto max-w-4xl text-center">
                            <h2
                                id="city-about-title"
                                className="text-xl font-myYekanFaNumExtraBold text-gray-950 tablet:text-2xl"
                            >
                                درباره {decodedCity}
                            </h2>
                            <div className="prose prose-sm mx-auto mt-5 max-w-none text-justify font-myYekanFaNumRegular leading-7 text-black tablet:prose-base
                                prose-p:text-justify prose-p:text-black prose-li:text-black
                                prose-headings:font-myYekanDemibold prose-headings:text-black
                                prose-strong:text-black prose-strong:font-myYekanDemibold
                                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:underline
                                prose-li:marker:text-gray-400">
                                <ReactMarkdown>{cityData.description}</ReactMarkdown>
                            </div>
                        </article>
                    </section>
                ) : null}


            </div>

            <Footer />
        </div>
    );
};

export default CityPage;
